import Image from '@atlaskit/image';
import type { SmartPrompt } from '@atlassian/conversation-assistant-blank-object-experience/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl-next';

import { fetchWithMemoryCache } from '@canvas/connection';
import { captureException } from '@canvas/sentry-react';

import calendarIcon from '../assets/smart-prompts/smart-prompt-calendar.svg';
import checklistIcon from '../assets/smart-prompts/smart-prompt-checklist.svg';
import customerIcon from '../assets/smart-prompts/smart-prompt-customer.svg';
import flowchartIcon from '../assets/smart-prompts/smart-prompt-flowchart.svg';
import guidelinesIcon from '../assets/smart-prompts/smart-prompt-guidelines.svg';
import iconsPeopleIcon from '../assets/smart-prompts/smart-prompt-icons-people.svg';
import lightbulbIcon from '../assets/smart-prompts/smart-prompt-lightbulb.svg';
import marketingIcon from '../assets/smart-prompts/smart-prompt-marketing.svg';
import projectManagementIcon from '../assets/smart-prompts/smart-prompt-project-management.svg';
import resilienceIcon from '../assets/smart-prompts/smart-prompt-resilience.svg';
import trustIcon from '../assets/smart-prompts/smart-prompt-trust.svg';
import userTestingIcon from '../assets/smart-prompts/smart-prompt-user-testing.svg';
import volunteeringIcon from '../assets/smart-prompts/smart-prompt-volunteering.svg';

import {
  DATABASE_SMART_PROMPT_ENTRIES,
  type DatabaseSmartPromptEntry
} from './database-smart-prompt-entries';

// Mirrors the job-title-to-role mapping in createWithRovoSmartPrompts.tsx
// Source: confluence/next/packages/blank-page-quick-actions/src/createWithRovoSmartPrompts.tsx
const keywordToCategoryMap: ReadonlyArray<readonly [string, string]> = [
  ['engineer', 'engineering'],
  ['developer', 'engineering'],
  ['programmer', 'engineering'],
  ['product', 'productManagement'],
  ['pm', 'productManagement'],
  ['marketing', 'marketing'],
  ['brand', 'marketing'],
  ['campaign', 'marketing'],
  ['sales', 'sales'],
  ['account', 'sales'],
  ['program', 'programManagement'],
  ['project manager', 'programManagement']
];

const getJobCategoryForTitle = (jobTitle: string | undefined): string => {
  if (!jobTitle) {
    return 'other';
  }
  const lowerTitle = jobTitle.toLowerCase();
  for (const [keyword, category] of keywordToCategoryMap) {
    if (lowerTitle.includes(keyword)) {
      return category;
    }
  }
  return 'other';
};

const createIconComponent = (iconUrl: string): SmartPrompt['icon'] => {
  const SmartPromptIcon = ({ size }: { size?: string; color?: string } = {}) => {
    const dimension = size === 'small' ? 20 : 100;
    return React.createElement(Image, {
      src: iconUrl,
      alt: '',
      width: dimension,
      height: dimension
    });
  };
  SmartPromptIcon.displayName = 'SmartPromptIcon';
  return SmartPromptIcon;
};

const personalizedPromptIcons: Record<string, SmartPrompt['icon']> = {
  calendar: createIconComponent(calendarIcon),
  checklist: createIconComponent(checklistIcon),
  customer: createIconComponent(customerIcon),
  develop: createIconComponent(flowchartIcon),
  flowchart: createIconComponent(flowchartIcon),
  guidelines: createIconComponent(guidelinesIcon),
  'icons-people': createIconComponent(iconsPeopleIcon),
  lightbulb: createIconComponent(lightbulbIcon),
  marketing: createIconComponent(marketingIcon),
  'project-management': createIconComponent(projectManagementIcon),
  'release-phases': createIconComponent(projectManagementIcon),
  resilience: createIconComponent(resilienceIcon),
  trust: createIconComponent(trustIcon),
  'user-testing': createIconComponent(userTestingIcon),
  volunteering: createIconComponent(volunteeringIcon)
};

const fallbackPersonalizedPromptIcon = createIconComponent(projectManagementIcon);

const normalizePersonalizedPromptIconKey = (iconKey: string): string =>
  iconKey
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/^smart-prompt-/, '');

const resolveDatabasePersonalizedPromptIcon = (
  iconKey: string
): SmartPrompt['icon'] | undefined =>
  personalizedPromptIcons[normalizePersonalizedPromptIconKey(iconKey)] ??
  fallbackPersonalizedPromptIcon;

const toSmartPrompt = (
  entry: DatabaseSmartPromptEntry,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt => ({
  name: entry.name,
  text: entry.prompt,
  label: formatMessage(entry.templateName),
  icon: createIconComponent(entry.icon),
  templateSnapshot: { default: '', dark: '' },
  prompt: entry.prompt,
  templateName: entry.templateName
});

const getDatabaseSmartPrompts = (
  jobTitle: string | undefined,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt[] => {
  const jobCategory = getJobCategoryForTitle(jobTitle);

  const primaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) => e.userRole === jobCategory && e.cardLevel === 'primary'
  );

  const secondaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) =>
      (e.userRole === jobCategory && e.cardLevel === 'secondary') ||
      (e.userRole === 'other' && (jobCategory !== 'other' || e.cardLevel !== 'primary'))
  );

  return [...primaryEntries, ...secondaryEntries]
    .slice(0, 8)
    .map((entry) => toSmartPrompt(entry, formatMessage));
};

export interface UseDatabaseSmartPromptsResult {
  smartPrompts: SmartPrompt[];
  resolvePersonalizedPromptIcon: (iconKey: string) => SmartPrompt['icon'] | undefined;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Fetches the current user's job title from the Confluence REST API and returns
 * smart prompts filtered by their role. Returns an empty array while loading to
 * avoid a flash of generic "other" prompts before the job title resolves.
 * On error, falls back to the generic "other" prompts so the modal is still useful.
 */
export const useDatabaseSmartPrompts = (): UseDatabaseSmartPromptsResult => {
  const { formatMessage } = useIntl();
  const [jobTitle, setJobTitle] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchJobTitle = async () => {
      try {
        const response = await fetchWithMemoryCache(
          '/gateway/api/me',
          {},
          { cacheKey: 'identity' }
        );
        if (!response.ok) {
          if (!cancelled) {
            setHasError(true);
            setIsLoading(false);
          }
          return;
        }
        const data = await response.json();
        if (!cancelled) {
          setJobTitle(data?.extended_profile?.job_title || undefined);
          setIsLoading(false);
        }
      } catch (e) {
        captureException(e, { tags: { captureLocation: 'useDatabaseSmartPrompts' } });
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchJobTitle();
    return () => {
      cancelled = true;
    };
  }, []);

  // When loading, return empty prompts to avoid a flash of "other" prompts.
  // On error, jobTitle remains undefined, which getJobCategoryForTitle maps to
  // 'other', so we naturally fall back to the generic prompts.
  const smartPrompts = useMemo(
    () => (isLoading ? [] : getDatabaseSmartPrompts(jobTitle, formatMessage)),
    [isLoading, jobTitle, formatMessage]
  );

  return {
    smartPrompts,
    resolvePersonalizedPromptIcon: resolveDatabasePersonalizedPromptIcon,
    isLoading,
    hasError
  };
};
